// src/App.js
import React, { useEffect, useState, useMemo } from 'react';
import UpperBar from './components/UpperBar';
import Graph from './components/Graph';
import RelationsPanel from './components/RelationsPanel';
import FilterBar from './components/FilterBar';
import Sidebar from './components/Sidebar';
import { loadGraph } from './dataSource';
import { detectProtocol, protocolCounts } from './protocols';

function App() {
    const [allNodes, setAllNodes] = useState([]);
    const [allEdges, setAllEdges] = useState([]);
    const [elements, setElements] = useState([]);
    const [highlighted, setHighlighted] = useState(null);
    const [navHistory, setNavHistory] = useState([]);
    const [viewingRfc, setViewingRfc] = useState(null);
    const [selectedProtocol, setSelectedProtocol] = useState('');

    const rfcNodes = useMemo(
        () => allNodes.filter(n => n?.data?.id?.startsWith('RFC')),
        [allNodes]
    );
    const counts = useMemo(() => protocolCounts(rfcNodes), [rfcNodes]);
    const nodeById = useMemo(() => new Map(allNodes.map(n => [n.data.id, n])), [allNodes]);

    // The chosen protocol's RFCs that actually have relations (connected).
    const connectedProtocolRfcs = useMemo(() => {
        if (!selectedProtocol) return [];
        const hasEdge = id => allEdges.some(e => e.data.source === id || e.data.target === id);
        return rfcNodes
            .filter(n => detectProtocol(n) === selectedProtocol && hasEdge(n.data.id))
            .sort((a, b) => a.data.number - b.data.number);
    }, [rfcNodes, allEdges, selectedProtocol]);

    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') setViewingRfc(null); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    useEffect(() => {
        loadGraph()
            .then(({ nodes, edges }) => { setAllNodes(nodes); setAllEdges(edges); })
            .catch(err => console.error(err));
    }, []);

    const handleSelect = (rfc) => {
        setNavHistory(h => (highlighted ? [...h, highlighted] : h));
        setHighlighted(rfc);
    };

    const goBack = () => {
        setNavHistory(h => {
            if (!h.length) return h;
            setHighlighted(h[h.length - 1]);
            return h.slice(0, -1);
        });
    };

    // Pick a protocol -> center on its most connected RFC (the hub).
    useEffect(() => {
        if (!selectedProtocol) return;
        const protoRfcs = rfcNodes.filter(n => detectProtocol(n) === selectedProtocol);
        const degree = id => allEdges.reduce((s, e) => s + (e.data.source === id || e.data.target === id ? 1 : 0), 0);
        const hub = [...protoRfcs].sort(
            (a, b) => degree(b.data.id) - degree(a.data.id) || (b.data.number - a.data.number)
        )[0];
        if (hub) { setNavHistory([]); setHighlighted(hub); }
    }, [selectedProtocol, rfcNodes, allEdges]);

    // Graph = focused RFC + its DIRECT relations (one hop).
    useEffect(() => {
        const rfcId = highlighted?.data?.id;
        if (!rfcId) { setElements([]); return; }
        const connected = allEdges.filter(e => e?.data?.source === rfcId || e?.data?.target === rfcId);
        const ids = new Set([rfcId]);
        connected.forEach(e => { ids.add(e.data.source); ids.add(e.data.target); });
        const subNodes = allNodes.filter(n => ids.has(n?.data?.id));
        setElements([...subNodes, ...connected]);
    }, [highlighted, allNodes, allEdges]);

    return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Top: search any RFC directly (no scrolling needed) */}
            <div style={{ height: '60px', flexShrink: 0 }}>
                <UpperBar rfcs={rfcNodes} onSelect={handleSelect} />
            </div>

            {/* Row: left protocols | center graph | right relations / viewer */}
            <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                <div style={{ width: '230px', flexShrink: 0, borderRight: '1px solid #ccc', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <FilterBar counts={counts} value={selectedProtocol} onChange={setSelectedProtocol} />
                    {selectedProtocol && (
                        <>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#777', padding: '8px 12px 4px' }}>
                                {selectedProtocol} — {connectedProtocolRfcs.length} connected RFCs
                            </div>
                            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                                <Sidebar rfcs={connectedProtocolRfcs} onSelect={handleSelect} />
                            </div>
                        </>
                    )}
                </div>

                <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                    <Graph
                        elements={elements}
                        selected={highlighted}
                        onNodeClick={(id) => {
                            if (id === highlighted?.data?.id) setViewingRfc(id);
                            else if (nodeById.has(id)) handleSelect(nodeById.get(id));
                        }}
                    />
                </div>

                {viewingRfc ? (
                    <div style={{ width: 460, flexShrink: 0, borderLeft: '1px solid #ccc', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ padding: '8px 12px', background: '#f7f7f7', borderBottom: '1px solid #ccc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <strong>{viewingRfc}</strong>
                            <button onClick={() => setViewingRfc(null)} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer' }}>✕</button>
                        </div>
                        <iframe
                            key={viewingRfc}
                            src={`https://www.rfc-editor.org/rfc/${viewingRfc.toLowerCase()}`}
                            style={{ flex: 1, border: 'none' }}
                            title={viewingRfc}
                        />
                    </div>
                ) : (
                    <RelationsPanel
                        selected={highlighted}
                        allNodes={allNodes}
                        allEdges={allEdges}
                        onSelect={handleSelect}
                        onBack={goBack}
                        canGoBack={navHistory.length > 0}
                    />
                )}
            </div>
        </div>
    );
}

export default App;
